import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowRight, Calendar } from 'lucide-react';
import { getPublishedBlogsByProfileId, getUsernameFromProfileId } from '../../../../services/firestoreService';

import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

/**
 * BlogRenderer - Renders the blog posts block
 * Fetches published blogs and displays them with theme styling
 */
const BlogRenderer = ({ block, profileId, theme = {}, isOnOwnDomain = false }) => {
  const { content } = block;
  const {
    heading = 'Latest Posts',
    maxPosts = 3,
    showViewAll = true,
  } = content;

  const [blogs, setBlogs] = useState([]);
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(true);

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  useEffect(() => {
    const loadBlogs = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        const [fetchedBlogs, fetchedUsername] = await Promise.all([
          getPublishedBlogsByProfileId(profileId),
          getUsernameFromProfileId(profileId),
        ]);

        // Sort by publish date (most recent first) and limit
        const sortedBlogs = (fetchedBlogs || [])
          .sort((a, b) => {
            const dateA = a.publishedAt?.toDate?.() || new Date(a.publishedAt);
            const dateB = b.publishedAt?.toDate?.() || new Date(b.publishedAt);
            return dateB - dateA;
          })
          .slice(0, maxPosts);

        setBlogs(sortedBlogs);
        setUsername(fetchedUsername);
      } catch (error) {
        console.error('Error loading blogs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [profileId, maxPosts]);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp?.toDate?.() || new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Placeholder when no blogs
  if (!loading && blogs.length === 0) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${styles.accentColor}20` }}
          >
            <FileText size={32} style={{ color: styles.accentColor }} />
          </div>
          <h3
            className="text-xl font-semibold mb-2"
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            No Blog Posts Yet
          </h3>
          <p style={{ color: styles.mutedColor }}>
            Blog posts will appear here once published.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-3 border-gray-600 rounded-full" style={{ borderTopColor: styles.accentColor }}></div>
        </div>
      </div>
    );
  }

  const hasMoreBlogs = blogs.length >= maxPosts;

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        {heading && (
          <div className="flex items-center justify-between mb-8">
            <h2
              className={`${styles.headingSizeClass} font-bold`}
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h2>
            {showViewAll && hasMoreBlogs && username && (
              <Link
                to={isOnOwnDomain ? `/blog` : `/me/${username}/blog`}
                className="flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-80"
                style={{ color: styles.accentColor }}
              >
                View All
                <ArrowRight size={16} />
              </Link>
            )}
          </div>
        )}

        {/* Blog Carousel - Horizontal Swipe */}
        <div
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {blogs.map((blog) => (
            <Link
              key={blog.id}
              to={isOnOwnDomain ? `/blog/${blog.slug || blog.id}` : `/me/${username}/blog/${blog.slug || blog.id}`}
              className="group flex-none w-[280px] sm:w-[320px] rounded-xl overflow-hidden transition-all hover:translate-y-[-2px] snap-start"
              style={{ border: `1px solid ${styles.borderColor}` }}
            >
              {/* Blog Image */}
              <div className="aspect-video overflow-hidden">
                {blog.imageUrl ? (
                  <img
                    src={blog.imageUrl}
                    alt={blog.title}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ backgroundColor: `${styles.accentColor}10` }}
                  >
                    <FileText size={40} style={{ color: styles.mutedColor }} />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Date */}
                <div className="flex items-center gap-2 mb-2" style={{ color: styles.mutedColor }}>
                  <Calendar size={12} />
                  <span className="text-xs">{formatDate(blog.publishedAt || blog.createdAt)}</span>
                </div>

                {/* Title */}
                <h3
                  className="text-base font-semibold mb-2 line-clamp-2 group-hover:underline"
                  style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
                >
                  {blog.title}
                </h3>

                {/* Excerpt */}
                {blog.excerpt && (
                  <p
                    className="text-sm line-clamp-2"
                    style={{ color: styles.bodyColor }}
                  >
                    {blog.excerpt}
                  </p>
                )}
              </div>
            </Link>
          ))}

          {/* View All Card */}
          {showViewAll && hasMoreBlogs && username && (
            <Link
              to={isOnOwnDomain ? `/blog` : `/me/${username}/blog`}
              className="group flex-none w-[280px] sm:w-[320px] rounded-xl overflow-hidden transition-all hover:translate-y-[-2px] snap-start flex items-center justify-center"
              style={{
                border: `1px solid ${styles.borderColor}`,
                minHeight: '200px',
              }}
            >
              <div className="text-center p-6">
                <div
                  className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${styles.accentColor}20` }}
                >
                  <ArrowRight size={24} style={{ color: styles.accentColor }} />
                </div>
                <span
                  className="font-semibold"
                  style={{ color: styles.accentColor }}
                >
                  View All Posts
                </span>
              </div>
            </Link>
          )}
        </div>

        {/* Hide scrollbar CSS */}
        <style>{`
          .overflow-x-auto::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    </div>
  );
};

export default BlogRenderer;
